/**
 * src/components/ui/Field.jsx — shared label/hint/error wrapper for every
 * form control (Input, Textarea, Select, DateField, TimeField).
 *
 * Two layers of "validate after interaction," per REDESIGN.md's Accessibility
 * standard, working together:
 *  1. A free, automatic visual layer: ui.css styles every `:user-invalid`
 *     input/textarea/select (modern-web-guidance's `validate-input-after-
 *     interaction`/`required-field-feedback` guides — Baseline Widely
 *     Available since 2023-11-02, no fallback needed per this project's
 *     browser support policy). `:user-invalid` only matches *after* a blur
 *     or submit attempt, never on first render or first keystroke — so
 *     every native constraint (`required`, `type="email"`, `pattern`) gets
 *     correctly-timed border/background feedback with zero JS.
 *  2. An explicit `error` message layer, controlled by the caller (same
 *     pattern `ConsentSign.jsx` already uses) — for app-level or
 *     server-returned errors a native HTML constraint can't express. Shown
 *     with `role="alert"` so its appearance is announced, exactly when the
 *     caller decides to show it (typically on blur or submit, not on mount).
 *
 * `:user-invalid` is a pure CSS/visual state and does not update
 * `aria-invalid` on its own — `syncAriaInvalid` below is the JS bridge both
 * guides call MANDATORY, so a screen reader user gets the same
 * "invalid after interaction" signal a sighted user sees from the border
 * colour change. Wire it to an input's `onBlur`/`onInput` to use it.
 */
import { useId } from 'react';

/**
 * Syncs `aria-invalid` to the live `:user-invalid` CSS state on blur/input,
 * per modern-web-guidance's accessible-error-announcement guide. Attach to
 * the input's `onBlur`/`onInput`.
 *
 * @param {React.SyntheticEvent<HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement>} e
 */
function syncAriaInvalid(e) {
  const el = e.currentTarget;
  if (el.matches(':user-invalid')) {
    el.setAttribute('aria-invalid', 'true');
  } else {
    el.removeAttribute('aria-invalid');
  }
}

export { syncAriaInvalid };

/**
 * Wraps a single form control with a label, optional hint, and optional
 * error message. Renders no control itself — pass one as `children`, with
 * the ids this component generates.
 *
 * @param {{
 *   label: string,
 *   hint?: React.ReactNode,
 *   error?: string|null,
 *   required?: boolean,
 *   htmlFor?: string,
 *   children: (ids: { inputId: string, hintId?: string, errorId?: string }) => React.ReactNode,
 * }} props
 * @returns {JSX.Element}
 */
export function Field({ label, hint, error, required, htmlFor, children }) {
  const generatedId = useId();
  const inputId = htmlFor ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = `${inputId}-error`;

  return (
    <div className="ui-field">
      <label htmlFor={inputId}>
        {label}
        {required ? (
          <span className="ui-field-required" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </label>
      {/* Hints sit ABOVE the control (modern-web-guidance: autofill/mobile-
          keyboard popovers cover text below a field while it's focused). */}
      {hint ? (
        <p id={hintId} className="ui-field-hint">
          {hint}
        </p>
      ) : null}
      {children({ inputId, hintId, errorId })}
      {/* role="alert" is what makes this announced the moment it appears —
          it only appears after :user-invalid has matched (on blur/submit),
          so a screen reader user never hears it on first render. */}
      {error ? (
        <p id={errorId} className="ui-field-error" role="alert">
          <span aria-hidden="true">⚠</span> {error}
        </p>
      ) : null}
    </div>
  );
}
